import { useEffect, useRef } from 'react';

/**
 * Whether a keystroke is being typed *into* something.
 *
 * ⚠️ **This function is the whole reason the hook exists.** The prototype binds `A`/`R`/`J`/`K`
 * on `window`, guarded only by route and modal state — so typing the letter `a` into any text
 * field on the matches route **fires a merge confirm**, on the screen that writes merges to a
 * customer's CRM ([B-72](../../../docs/platform/TODO.md), census §4a). `apps/web` responded by
 * not shipping the keys at all, which is safe and also means the product's stated keyboard model
 * (a11y.md §3) has been documented and unimplemented since it was written.
 *
 * `isContentEditable` covers a rich-text host; `closest()` covers a keystroke landing on a child
 * of one, which `event.target.isContentEditable` alone does not — the editable host is the
 * ancestor, not necessarily the node the event fires on.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return target.closest('[contenteditable]:not([contenteditable="false"])') !== null;
}

export interface ReviewQueueHandlers {
  /** `J` and `.` — advance. */
  onNext?: () => void;
  /** `K` — go back. */
  onPrevious?: () => void;
  /**
   * `A` — confirm. ⚠️ Destructive-adjacent: this is what the input guard protects.
   * Omit it and the key is not bound at all, rather than bound to nothing.
   */
  onApprove?: () => void;
  /** `R` — reject. */
  onReject?: () => void;
  /**
   * Turns the whole model off — while a modal is open, while a decision is in flight, or on a
   * screen state with no pair selected. **Checked at event time, not at bind time**, so a
   * keystroke during an in-flight apply cannot queue a second one behind it.
   */
  disabled?: boolean;
  /**
   * Announced to a screen reader after a handler runs, e.g. `"Approved. Next pair."`
   *
   * a11y.md §3 requires it in the same paragraph as the key table: a sighted power user gets
   * feedback from the queue advancing visually, and without this a screen-reader user gets a
   * silent state change — the same action with none of the confirmation.
   */
  announce?: (message: string) => void;
}

/**
 * The match review queue's keyboard model — a11y.md §3's `J`/`K`/`A`/`R`/`.` table, with the
 * input-focus guard the prototype's version lacks.
 *
 * It lives in the catalogue rather than in one screen because a11y.md is explicit that "every
 * other list-with-bulk-action screen (bulk approve, action history, findings) follows the same
 * up/down + primary-action keyboard shape … rather than inventing a per-screen scheme" — and a
 * shape re-implemented per screen is a guard re-implemented per screen.
 *
 * ⚠️ **Modifier chords are ignored, not handled.** `Cmd-R` is reload and `Ctrl-A` is select-all;
 * swallowing either to mean "reject"/"approve" would be a worse defect than the one this fixes.
 *
 * ⚠️ **Bound on `document`, and the guard is the scope.** Scoping the listener to a container
 * element instead was tried in principle and rejected: the queue's own focus lives on buttons
 * inside it, but a power user's hands are on the keys with focus on `<body>`, which no container
 * listener ever receives.
 */
export function useReviewQueueKeys({
  onNext,
  onPrevious,
  onApprove,
  onReject,
  disabled = false,
  announce,
}: ReviewQueueHandlers): void {
  /* Handlers change every render (they close over the current pair). Keeping them in a ref means
     the listener is attached once rather than re-bound on each keystroke's re-render — which
     also means a key held down cannot race a re-bind. */
  const latest = useRef<ReviewQueueHandlers>({});
  latest.current = { onNext, onPrevious, onApprove, onReject, disabled, announce };

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const h = latest.current;
      if (h.disabled) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;

      const key = event.key.toLowerCase();
      const run = (fn: (() => void) | undefined, message: string) => {
        if (!fn) return;
        event.preventDefault();
        fn();
        h.announce?.(message);
      };

      if (key === 'j' || key === '.') run(h.onNext, 'Next pair.');
      else if (key === 'k') run(h.onPrevious, 'Previous pair.');
      else if (key === 'a') run(h.onApprove, 'Approved. Next pair.');
      else if (key === 'r') run(h.onReject, 'Rejected. Next pair.');
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
}
